import { ChangeDetectionStrategy, Component, Input, OnInit, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { Alignment } from '@features/spots/umbraco-block-list-spot/block-list-spot.config';
import { SignUpSignInSpot } from '@features/spots/umbraco-block-list-spot/block-list-spot.models';
import { CustomValidatorsService } from '@shared/form-elements/custom-validators/custom-validators.service';
import { FormModelControls } from '@shared/form-elements/model/form-model-controls';
import { TranslateSafePipe } from '@shared/translate-safe/translate-safe.pipe';
import { SafePipe } from '@shared/safe/safe.pipe';
import { IconButtonComponent } from '@shared/button/icon-button/icon-button.component';
import { InputEmailComponent } from '@shared/form-elements/input/input-email/input-email.component';
import { HandleInvalidOnSubmitDirective } from '@shared/form-elements/form-helpers/handle-invalid-on-submit.directive';
import { InterceptLinksDirective } from '@libs/routing/dynamic-routing/intercept-links.directive';
import { SpotHeadlineComponent } from '../spot-headline/spot-headline.component';
import { NgClass, NgIf } from '@angular/common';
import { LoadingDotsComponent } from '@shared/loading-dots/loading-dots.component';
import { AuthMode } from '@core/authentication/authentication.models';
import { SvgIconComponent } from '@shared/svg-icon/svg-icon.component';
import { timer } from 'rxjs';
import { take } from 'rxjs/operators';

type FormModel = { username: string };

@Component({
    selector: 'app-sign-up-sign-in',
    templateUrl: './sign-up-sign-in.component.html',
    styleUrls: ['./sign-up-sign-in.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        LoadingDotsComponent,
        NgClass,
        NgIf,
        SpotHeadlineComponent,
        InterceptLinksDirective,
        FormsModule,
        HandleInvalidOnSubmitDirective,
        ReactiveFormsModule,
        InputEmailComponent,
        IconButtonComponent,
        SafePipe,
        TranslateSafePipe,
        SvgIconComponent
    ]
})
export class SignUpSignInComponent implements OnInit {
    public static readonly ref: string = 'signUpSignIn';

    private loginSource: AuthMode = 'AzureB2C';

    public alignment = Alignment;
    public form: UntypedFormGroup;
    public loggingIn = signal(false);

    @Input()
    public data: SignUpSignInSpot;

    constructor(
        public authenticationService: AuthenticationService,
        private formBuilder: UntypedFormBuilder,
        private customValidatorsService: CustomValidatorsService
    ) {}

    ngOnInit(): void {
        const formControls: FormModelControls<FormModel> = {
            username: ['', [Validators.required, this.customValidatorsService.email]]
        };
        this.form = this.formBuilder.group(formControls);
    }

    public loginWithVipps(): void {
        this.authenticationService.login('Vipps');
    }

    public onSubmit(): void {
        if (!this.form.valid) {
            return;
        }

        this.loggingIn.set(true);
        this.form.disable();
        this.authenticationService.login(this.loginSource, (this.form.value as FormModel).username);

        // when we call the azure journey we are redirected to the b2c login page, but when we come back,
        // because in some situations we don't have any button to cancel the login process
        // so we need to use the back button, and that button will load the page again,
        // but in the previous state, so we need to make sure the loggingIn state is set to false
        // and the form is enabled again to not see the loading dots
        timer(500)
            .pipe(take(1))
            .subscribe(() => {
                this.loggingIn.set(false);
                this.form.enable();
            });
    }
}
